import React, { useEffect, useMemo, useState } from 'react';
import Select, {
  components,
  GroupBase,
  MenuListProps,
  NoticeProps,
} from 'react-select';
import { Tooltip } from 'react-tooltip';
import { FixedSizeList } from 'react-window';
import RequiredSign from '../required-sign';

interface Props {
  id: string;
  options: any[];
  onSelect: (selectedOption: any) => void;
  dummyOption?: boolean;
  dummyOptionText?: string;
  placeholder?: string;
  selectedValue?: any;
  dontShowAlreadySelected?: boolean;
  label?: string;
  className?: string;
  isDisabled?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  required?: boolean;
}

function SearchDropdown({
  id,
  options,
  onSelect,
  dummyOption,
  dummyOptionText,
  placeholder,
  selectedValue,
  dontShowAlreadySelected,
  label,
  className,
  isDisabled,
  isSearchable = true,
  isClearable = false,
  required,
}: Props) {
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const handleSelectChange = (selected: any) => {
    setSelectedOption(selected);
    onSelect(selected);
  };

  const handleInputChange = (inputValue: string) => {
    setSearchTerm(inputValue);
  };

  const NoOptionsMessage: React.FC<
    NoticeProps<any, boolean, GroupBase<unknown>>
  > = (props) => (
    <components.NoOptionsMessage {...props}>
      Loading...
    </components.NoOptionsMessage>
  );

  const sanitizedOptions = options || [];

  const hasDummyOption = sanitizedOptions.some(
    (option) => option.label === 'Select a value'
  );

  if (dummyOption && !hasDummyOption) {
    sanitizedOptions.unshift({ label: 'Select a value', value: null });
  }

  if (dummyOptionText) {
    const hasDummyOptionText = sanitizedOptions.some(
      (option) => option.label === dummyOptionText
    );
    if (!hasDummyOptionText) {
      sanitizedOptions.unshift({ label: dummyOptionText, value: '' });
    }
  }
  const filteredOptions = sanitizedOptions.filter(
    (option) =>
      option.label &&
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      border: isDisabled
        ? 'none'
        : selectedOption
        ? '1px solid black !important'
        : '1px solid #E2E8F0 !important',
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#212529 !important',
      boxShadow: state.isFocused
        ? '0 0 0 0.3rem rgba(173, 216, 230, 0.50)'
        : 'none',
      backgroundColor: isDisabled ? '#e0e0e4' : '#ffffff',
      paddingTop: '3px',
      paddingBottom: '3px',
      fontFamily: 'Lexend',
      height: 48,
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided: any, state: any) => ({
      ...provided,
      '& svg': {
        width: '20px',
        height: '20px',
        color: '#25215F',
      },
    }),
  };

  const optionStyles = {
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isFocused ? '#ddd' : 'transparent',
      padding: '8px 20px',
      fontSize: 14,
      zIndex: 1,
      color: '#212529',
    }),
  };

  const MenuList = (props: MenuListProps<any, boolean, GroupBase<unknown>>) => {
    const { maxHeight, ...rest } = props;
    const menuHeight = Math.min(maxHeight, 200); // Set a max height for the menu
    return (
      <components.MenuList {...rest} maxHeight={menuHeight}>
        {props.children}
      </components.MenuList>
    );
  };

  useEffect(() => {
    if (options && selectedValue) {
      let selected: any = {};
      if (typeof selectedValue === 'string') {
        selected = options.find(
          (option) => option && option.value === selectedValue
        );
      } else {
        if (selectedValue.value) {
          selected = selectedValue;
        } else {
          selected = options.find(
            (option) =>
              option.value && option?.value?.id === selectedValue[0]?.id
          );
        }
      }
      setSelectedOption(selected);
    }
    if (
      options &&
      !dontShowAlreadySelected &&
      (selectedValue === undefined ||
        selectedValue === '' ||
        selectedValue === null)
    ) {
      setSelectedOption(filteredOptions[0]);
    }
  }, [options, selectedValue]);

  return (
    <div>
      {/* <Tooltip id='my-tooltip' /> */}
      {label && (
        <label
          // data-tooltip-id='my-tooltip'
          // data-tooltip-content={selectedOption ? selectedOption : placeholder}
          className='dropdown-label'
          htmlFor={id}
        >
          {label}
          {required && <RequiredSign />}
        </label>
      )}

      <Select
        id={id}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        className={className}
        value={selectedOption}
        defaultValue={selectedOption}
        onChange={handleSelectChange}
        onInputChange={handleInputChange}
        options={filteredOptions}
        components={{
          NoOptionsMessage,
          MenuList,
        }}
        styles={{ ...customStyles, ...optionStyles }}
        placeholder={placeholder ? placeholder : 'Select a value'}
        // menuPortalTarget={document.body}
      />
    </div>
  );
}

export default SearchDropdown;
